// app/frontend/components/community/ReactionPicker.tsx
import { useState, useRef, useCallback } from "react";
import { Group, ActionIcon, Popover, Tooltip, Box, Stack } from "@mantine/core";
import { L2KCaption } from "../ui/Typography";

const REACTIONS = [
  { emoji: "👍", label: "Like", hasSkinTones: true },
  { emoji: "❤️", label: "Love", hasSkinTones: false },
  { emoji: "🎉", label: "Celebrate", hasSkinTones: false },
  { emoji: "🚀", label: "Rocket", hasSkinTones: false },
] as const;

const THUMBS_UP_SKIN_TONES = ["👍", "👍🏻", "👍🏼", "👍🏽", "👍🏾", "👍🏿"] as const;

// Check if emoji is a thumbs up variant
const isThumbsUpVariant = (emoji: string) => THUMBS_UP_SKIN_TONES.includes(emoji as typeof THUMBS_UP_SKIN_TONES[number]);

interface ReactionsSummary {
  [emoji: string]: number;
}

interface ReactionPickerProps {
  reactableType: "Post" | "Comment";
  reactableId: number;
  userReaction: string | null;
  reactionsSummary: ReactionsSummary;
  reactionsCount: number;
  onReactionChange?: (newReaction: string | null, newSummary: ReactionsSummary, newCount: number) => void;
  size?: "xs" | "sm";
}

export function ReactionPicker({
  reactableType,
  reactableId,
  userReaction,
  reactionsSummary,
  reactionsCount,
  onReactionChange,
  size = "sm",
}: ReactionPickerProps) {
  const [opened, setOpened] = useState(false);
  const [showSkinTones, setShowSkinTones] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentReaction, setCurrentReaction] = useState(userReaction);
  const [currentSummary, setCurrentSummary] = useState(reactionsSummary || {});
  const [currentCount, setCurrentCount] = useState(reactionsCount);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);

  const handleReaction = async (emoji: string) => {
    setIsLoading(true);
    setOpened(false);
    setShowSkinTones(false);

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch("/community/reactions/toggle", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          reactable_type: reactableType,
          reactable_id: reactableId,
          emoji,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentReaction(data.emoji);
        setCurrentSummary(data.reactions_summary || {});
        setCurrentCount(data.reactions_count);
        onReactionChange?.(data.emoji, data.reactions_summary || {}, data.reactions_count);
      }
    } catch (error) {
      console.error("Failed to toggle reaction:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Long press handlers for skin tone selection
  const handleThumbsUpMouseDown = useCallback(() => {
    longPressTimer.current = setTimeout(() => {
      setShowSkinTones(true);
    }, 500); // 500ms long press
  }, []);

  const handleThumbsUpMouseUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleThumbsUpClick = useCallback(() => {
    if (!showSkinTones) {
      handleReaction("👍");
    }
  }, [showSkinTones]);

  const iconSize = size === "xs" ? 14 : 16;
  const buttonSize = size === "xs" ? "xs" : "sm";

  // Get reactions to display (sorted by count), grouping thumbs up variants
  const displayReactions = Object.entries(currentSummary)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <Group gap={4}>
      {/* Display existing reactions */}
      {displayReactions.length > 0 && (
        <Group
          gap={2}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            borderRadius: 12,
            padding: "2px 8px",
          }}
        >
          {displayReactions.map(([emoji, count]) => {
            const label = isThumbsUpVariant(emoji) ? "Like" : REACTIONS.find(r => r.emoji === emoji)?.label || emoji;
            const isCurrentReaction = currentReaction === emoji || (isThumbsUpVariant(emoji) && isThumbsUpVariant(currentReaction || ""));
            return (
              <Tooltip key={emoji} label={`${count} ${label}`}>
                <Box
                  component="button"
                  onClick={() => handleReaction(emoji)}
                  style={{
                    background: isCurrentReaction ? "rgba(255, 152, 0, 0.2)" : "transparent",
                    border: "none",
                    borderRadius: 4,
                    padding: "2px 4px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <span style={{ fontSize: iconSize }}>{emoji}</span>
                  <L2KCaption variant="tertiary" style={{ fontSize: 11 }}>
                    {count}
                  </L2KCaption>
                </Box>
              </Tooltip>
            );
          })}
        </Group>
      )}

      {/* Add reaction button */}
      <Popover
        opened={opened}
        onChange={setOpened}
        position="top"
        withArrow
        shadow="md"
      >
        <Popover.Target>
          <ActionIcon
            variant="subtle"
            size={buttonSize}
            color="gray"
            loading={isLoading}
            onClick={() => setOpened((o) => !o)}
            style={{
              color: currentReaction ? "#FF9800" : "#737373",
            }}
          >
            {currentReaction ? (
              <span style={{ fontSize: iconSize }}>{currentReaction}</span>
            ) : (
              <span style={{ fontSize: iconSize }}>👍</span>
            )}
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown
          style={{
            backgroundColor: "rgba(26, 26, 26, 0.98)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(20px)",
            padding: 8,
          }}
        >
          {showSkinTones ? (
            // Skin tone picker for thumbs up
            <Stack gap={4}>
              <L2KCaption variant="tertiary" style={{ fontSize: 10, textAlign: "center" }}>
                Choose skin tone
              </L2KCaption>
              <Group gap={4}>
                {THUMBS_UP_SKIN_TONES.map((emoji) => (
                  <ActionIcon
                    key={emoji}
                    variant={currentReaction === emoji ? "filled" : "subtle"}
                    size="lg"
                    color={currentReaction === emoji ? "orange" : "gray"}
                    onClick={() => handleReaction(emoji)}
                    style={{ fontSize: 20 }}
                  >
                    {emoji}
                  </ActionIcon>
                ))}
              </Group>
              <Box
                component="button"
                onClick={() => setShowSkinTones(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#737373",
                  fontSize: 11,
                  cursor: "pointer",
                  textAlign: "center",
                  marginTop: 4,
                }}
              >
                ← Back
              </Box>
            </Stack>
          ) : (
            // Regular reaction picker
            <Group gap={4}>
              {REACTIONS.map(({ emoji, label, hasSkinTones }) => (
                <Tooltip key={emoji} label={hasSkinTones ? `${label} (hold for skin tones)` : label}>
                  <ActionIcon
                    variant={isThumbsUpVariant(currentReaction || "") && emoji === "👍" ? "filled" : currentReaction === emoji ? "filled" : "subtle"}
                    size="lg"
                    color={isThumbsUpVariant(currentReaction || "") && emoji === "👍" ? "orange" : currentReaction === emoji ? "orange" : "gray"}
                    onClick={() => hasSkinTones ? handleThumbsUpClick() : handleReaction(emoji)}
                    onMouseDown={hasSkinTones ? handleThumbsUpMouseDown : undefined}
                    onMouseUp={hasSkinTones ? handleThumbsUpMouseUp : undefined}
                    onMouseLeave={hasSkinTones ? handleThumbsUpMouseUp : undefined}
                    onTouchStart={hasSkinTones ? handleThumbsUpMouseDown : undefined}
                    onTouchEnd={hasSkinTones ? handleThumbsUpMouseUp : undefined}
                    style={{ fontSize: 20 }}
                  >
                    {emoji === "👍" && isThumbsUpVariant(currentReaction || "") ? currentReaction : emoji}
                  </ActionIcon>
                </Tooltip>
              ))}
            </Group>
          )}
        </Popover.Dropdown>
      </Popover>

      {/* Total count if no individual reactions shown */}
      {displayReactions.length === 0 && currentCount > 0 && (
        <L2KCaption variant="tertiary">{currentCount}</L2KCaption>
      )}
    </Group>
  );
}
